import { MODEL_ALIAS_COPY } from "./copy.js";

export function managedMcpServerError(server) {
    if (!/^[A-Za-z0-9_-]+$/.test(server.name?.trim() ?? "")) return "Use letters, numbers, hyphens, or underscores for the server name.";
    if (server.type !== "http" && server.type !== "sse") return "Choose HTTP or SSE for the server type.";
    try {
        const url = new URL(server.url?.trim() ?? "");
        if (url.protocol !== "https:" || !url.hostname) return "Use a full HTTPS URL for the server.";
    } catch {
        return "Use a full HTTPS URL for the server.";
    }
    if (Object.values(server).some((value) => typeof value === "string" && (value.includes("${") || /[\u0000-\u001f\u007f-\u009f]/.test(value)))) {
        return "Use literal values without environment-variable placeholders or control characters.";
    }
    return null;
}

function suggestedValues(catalog, pointer) {
    return catalog?.settings?.find((setting) => setting.pointer === pointer)?.suggestedValues ?? [];
}

/// Values the Claude Code controls derive from the draft and the catalog.
export function deriveEditorValues(controls, catalog) {
    const modelAliases = suggestedValues(catalog, "/model");
    const values = controls.restrictModels ? controls.availableModels : modelAliases;
    const withImported = controls.startingModel && !values.includes(controls.startingModel)
        ? [...values, controls.startingModel]
        : values;
    const modelOptions = [
        { value: "", label: "Claude Code Default" },
        ...withImported.map((value) => ({ value, label: MODEL_ALIAS_COPY[value]?.label ?? `${value} (imported)` })),
    ];
    const hasServer = [...controls.mcpServers, ...(controls.mcpUrls ?? []), ...(controls.mcpCommands ?? [])].some((item) => item.trim());
    const mcpFilterWritten = controls.mcpMode === "block" || controls.mcpMode === "preserve"
        || ((controls.mcpMode === "allow" || controls.mcpMode === "deny") && hasServer);
    const marketplaceRestrictionWritten = controls.marketplaceMode === "block"
        || (controls.marketplaceMode === "allow" && controls.marketplaces.some((item) => item.trim()));
    // Named models for the effect line. An administrator reads "Sonnet or Opus",
    // not a count, because the count does not say which models a session gets.
    const labels = controls.availableModels.map((value) => MODEL_ALIAS_COPY[value]?.label ?? value);
    const modelListSummary = labels.length <= 2
        ? labels.join(" or ")
        : `${labels.slice(0, -1).join(", ")}, or ${labels[labels.length - 1]}`;
    return { modelAliases, modelOptions, mcpFilterWritten, marketplaceRestrictionWritten, modelListSummary };
}

/// Selected list controls that hold no entry. Each blocks the pilot, because an
/// empty list is omitted from the document.
export function incompleteLists(controls) {
    const incomplete = [];
    const hasMcpEntries = [...controls.mcpServers, ...(controls.mcpUrls ?? []), ...(controls.mcpCommands ?? [])].some((item) => item.trim());
    const hasMarketplaceEntries = controls.marketplaces.some((item) => item.trim());
    if ((controls.mcpMode === "allow" || controls.mcpMode === "deny") && !hasMcpEntries) incomplete.push("MCP server list");
    if (controls.marketplaceMode === "allow" && !hasMarketplaceEntries) incomplete.push("plugin marketplace list");
    if (controls.restrictModels && !controls.availableModels.length) incomplete.push("model allowlist");
    if (controls.managedMcpServersAuthorable && controls.managedMcpServers.some(managedMcpServerError)) incomplete.push("managed MCP server");
    const managedNames = controls.managedMcpServers.map((server) => server.name.trim()).filter(Boolean);
    if (controls.managedMcpServersAuthorable && new Set(managedNames).size !== managedNames.length) incomplete.push("duplicate managed MCP server name");
    return incomplete;
}
